import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useStore } from '@/hooks/useStore';
import {
  getState, getFarmerById, getFarmerKyc, getKycLabel, getNotifications, getLogsByFarmer, getSeasonsByFarmer, getContractsByFarmer,
  getFarmerMoneySummary,
  getFarmerRemainingLimit,
  getFarmerTotalDebt,
  getFarmerOverdueDebt,
  getFarmerOverdueCount,
  respondToHtxInvite,
} from '@/store';
import { showToast } from '@/components/base/Toast';
import { formatCurrencyFull, formatCurrencyCompact, formatNumber, calculateUsagePercent, getProgressColor, formatDate } from '@/utils/formatters';
import { getRiskColor } from '@/utils/status';
import { BottomSheet } from '@/components/base/BottomSheet';
import { ActionIconGrid } from '@/components/base/ActionIconGrid';
import { CompactAlert } from '@/components/base/CompactAlert';
import { ImportantMetric } from '@/components/base/ImportantMetric';
import { CurrencyAmount } from '@/components/base/CurrencyAmount';
import { FirstLoginQuickGuide } from '@/components/feature/FirstLoginQuickGuide';
import { ApplyLoanSheet } from '@/pages/farmer/info/components/ApplyLoanSheet';
import { ApplyLimitSheet } from '@/pages/farmer/info/components/ApplyLimitSheet';
import { QuickDiarySheet } from '@/pages/farmer/info/components/QuickDiarySheet';
import { RepayDebtSheet } from '@/pages/farmer/info/components/RepayDebtSheet';

// ─── Quick Action Config (Direct BottomSheet actions) ───────

interface QuickAction {
  id: string;
  icon: string;
  label: string;
  bgColor: string;
  iconColor: string;
}

// eKYC is only enforced at the point of actually submitting a cash loan
// (ApplyLoanSheet's own internal step gates that specific branch) — no quick
// action here blocks opening its sheet just because eKYC isn't done yet, so a
// standalone/new farmer can always reach vật tư loans, hạn mức, nhật ký, etc.
const farmerActions: QuickAction[] = [
  { id: 'loan', icon: 'ri-hand-coin-line', label: 'Xin vay HTX', bgColor: 'bg-agrida-50', iconColor: 'text-agrida-600' },
  { id: 'limit', icon: 'ri-scales-3-line', label: 'Xin hạn mức HTX', bgColor: 'bg-amber-50', iconColor: 'text-amber-600' },
  { id: 'track', icon: 'ri-file-list-3-line', label: 'Theo dõi khoản vay', bgColor: 'bg-sky-50', iconColor: 'text-sky-600' },
  { id: 'contracts', icon: 'ri-draft-line', label: 'Hợp đồng', bgColor: 'bg-lime-50', iconColor: 'text-lime-700' },
  { id: 'repay', icon: 'ri-secure-payment-line', label: 'Trả nợ HTX', bgColor: 'bg-rose-50', iconColor: 'text-rose-600' },
  { id: 'diary', icon: 'ri-book-open-line', label: 'Ghi nhật ký', bgColor: 'bg-teal-50', iconColor: 'text-teal-600' },
];

const farmerGuideDescriptions: Record<string, string> = {
  loan: 'Gửi yêu cầu vay trực tiếp đến HTX và theo dõi trạng thái phê duyệt, giải ngân.',
  limit: 'Xin cấp hoặc tăng hạn mức vay dựa trên hợp đồng bao tiêu đang hiệu lực.',
  track: 'Xem tất cả yêu cầu vay, khoản đang chờ giải ngân và lịch sử xử lý.',
  contracts: 'Xem hợp đồng bao tiêu, mùa vụ liên quan, giá trị hợp đồng và hạn mức đã cấp.',
  repay: 'Chọn khoản công nợ và gửi xác nhận thanh toán trực tiếp cho HTX.',
  diary: 'Ghi nhanh hoạt động canh tác để hoàn thiện hồ sơ sản xuất và đánh giá rủi ro.',
};

const farmerGuideItems = farmerActions.map((action) => ({
  ...action,
  colorClass: action.iconColor,
  backgroundClass: action.bgColor,
  description: farmerGuideDescriptions[action.id] || '',
}));

// ─── Activity Feed ──────────────────────────────────────────

interface Activity {
  id: string;
  icon: string;
  message: string;
  time: string;
  color: 'green' | 'red' | 'amber' | 'sky';
}

const activityStyles: Record<string, { iconBg: string; iconColor: string }> = {
  green: { iconBg: 'bg-agrida-50', iconColor: 'text-agrida-600' },
  red: { iconBg: 'bg-red-50', iconColor: 'text-red-600' },
  amber: { iconBg: 'bg-amber-50', iconColor: 'text-amber-600' },
  sky: { iconBg: 'bg-sky-50', iconColor: 'text-sky-600' },
};

function timeAgo(dateStr: string): string {
  const now = new Date();
  const then = new Date(dateStr);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Vừa xong';
  if (diffMins < 60) return `${diffMins} phút trước`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} giờ trước`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Hôm qua';
  return `${diffDays} ngày trước`;
}

function buildFarmerActivities(farmerId: string): Activity[] {
  const activities: Activity[] = [];
  const state = getState();

  // From farmer's notifications
  const notifs = getNotifications().filter((n) => n.farmerId === farmerId).slice(0, 5);
  notifs.forEach((n) => {
    const color: 'green' | 'red' | 'amber' =
      n.type === 'disburse_success' || n.type === 'loan_approved' ? 'green' :
      n.type === 'disburse_failed' || n.type === 'loan_rejected' ? 'red' : 'amber';
    const icon =
      n.type === 'disburse_success' || n.type === 'loan_approved' ? 'ri-check-double-line' :
      n.type === 'disburse_failed' || n.type === 'loan_rejected' ? 'ri-close-circle-line' :
      'ri-notification-3-line';
    activities.push({ id: n.id, icon, message: n.content, time: timeAgo(n.ngayTao), color });
  });

  // From farmer's diaries (most recent)
  const diaries = getLogsByFarmer(farmerId).sort((a, b) => new Date(b.ngay).getTime() - new Date(a.ngay).getTime()).slice(0, 4);
  diaries.forEach((d) => {
    activities.push({
      id: `diary-${d.id}`,
      icon: 'ri-file-list-line',
      message: `${d.hoatDong}${d.chiTiet ? ` - ${d.chiTiet.slice(0, 60)}${d.chiTiet.length > 60 ? '...' : ''}` : ''}`,
      time: timeAgo(d.ngay),
      color: 'sky',
    });
  });

  // From farmer's loans (recent status changes)
  const loans = state.advanceRequests
    .filter((l) => l.farmerId === farmerId && l.loai === 'Tiền')
    .sort((a, b) => {
      const aDate = a.ngayDuyet || a.ngayYeuCau;
      const bDate = b.ngayDuyet || b.ngayYeuCau;
      return new Date(bDate).getTime() - new Date(aDate).getTime();
    })
    .slice(0, 3);
  loans.forEach((l) => {
    if (['Đã duyệt', 'Chờ giải ngân', 'approved_locked', 'Đã giải ngân', 'disbursed'].includes(l.status)) {
      activities.push({
        id: `loan-${l.id}`,
        icon: 'ri-check-double-line',
        message: `${l.status === 'Đã giải ngân' || l.status === 'disbursed' ? 'Đã giải ngân' : 'Đã duyệt, chờ giải ngân'} khoản vay ${formatCurrencyCompact(l.soTien)}`,
        time: timeAgo(l.ngayDuyet || l.ngayYeuCau),
        color: 'green',
      });
    } else if (l.status === 'Từ chối') {
      activities.push({
        id: `loan-${l.id}`,
        icon: 'ri-close-circle-line',
        message: `Khoản vay ${formatCurrencyCompact(l.soTien)} bị từ chối`,
        time: timeAgo(l.ngayDuyet || l.ngayYeuCau),
        color: 'red',
      });
    }
  });

  // From farmer's debts (recent repayments)
  const debts = state.debts
    .filter((d) => d.farmerId === farmerId && d.status === 'Đã tất toán')
    .sort((a, b) => new Date(b.ngayDenHan).getTime() - new Date(a.ngayDenHan).getTime())
    .slice(0, 2);
  debts.forEach((d) => {
    activities.push({
      id: `debt-${d.id}`,
      icon: 'ri-checkbox-circle-line',
      message: `Đã tất toán khoản vay ${formatCurrencyCompact(d.soTien)}`,
      time: timeAgo(d.ngayDenHan),
      color: 'green',
    });
  });

  // Sort and take top 8
  activities.sort((a, b) => {
    if (a.time.includes('Vừa')) return -1;
    if (b.time.includes('Vừa')) return 1;
    const aNum = parseInt(a.time.replace(/\D/g, ''), 10) || 0;
    const bNum = parseInt(b.time.replace(/\D/g, ''), 10) || 0;
    if (a.time.includes('phút')) {
      if (!b.time.includes('phút')) return -1;
      return aNum - bNum;
    }
    if (b.time.includes('phút')) return 1;
    if (a.time.includes('giờ')) {
      if (b.time.includes('phút')) return 1;
      if (!b.time.includes('giờ')) return -1;
      return aNum - bNum;
    }
    if (b.time.includes('giờ')) return 1;
    if (a.time.includes('Hôm')) return -1;
    if (b.time.includes('Hôm')) return 1;
    return aNum - bNum;
  });

  return activities.slice(0, 8);
}

// ─── Page Component ─────────────────────────────────────────

export default function FarmerOverviewPage() {
  useStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const state = getState();

  const farmerId = user?.farmerId || 'f1';
  const farmer = getFarmerById(farmerId);
  const kyc = farmer ? getFarmerKyc(farmer) : null;
  const isKycVerified = kyc?.kycStatus === 'ekyc_verified';
  const isKycNeedMore = kyc?.kycStatus === 'ekyc_need_more';
  const isKycFailed = kyc?.kycStatus === 'ekyc_failed';
  const isKycPending = kyc?.kycStatus === 'ekyc_pending' || kyc?.kycStatus === 'registered';

  // ─── Money Stats ───
  // Not memoized: `getFarmerMoneySummary` reads a KeyedAsyncCache that's still
  // empty on the very first render after login. Memoizing on `farmerId` alone
  // froze that first all-zero snapshot forever (until a full remount), so
  // "Đã sử dụng"/"Tổng hạn mức" never updated once the real data arrived —
  // only `remainingLimit` below (never memoized) kept reading fresh, producing
  // two numbers computed at different points in time on the same screen.
  const moneySummary = getFarmerMoneySummary(farmerId);
  const remainingLimit = getFarmerRemainingLimit(farmerId);
  const totalLimit = moneySummary.hanMucTienTong;
  const limitPercent = totalLimit > 0 ? calculateUsagePercent(moneySummary.hanMucTienDaDung, totalLimit) : 0;
  const limitProgress = getProgressColor(limitPercent);
  const totalDebt = getFarmerTotalDebt(farmerId);
  const overdueDebt = getFarmerOverdueDebt(farmerId);
  const overdueCount = getFarmerOverdueCount(farmerId);

  // ─── Season Stats ───
  const farmerSeasons = getSeasonsByFarmer(farmerId);
  const activeSeasons = farmerSeasons.filter((s) => s.status === 'Đang canh tác' || s.status === 'Sắp thu hoạch');
  const preparingSeasons = farmerSeasons.filter((s) => s.status === 'Chuẩn bị');

  // ─── Contract Stats ───
  const farmerContracts = getContractsByFarmer(farmerId);
  const activeContracts = farmerContracts.filter((c) => c.status === 'Đang hiệu lực');

  // ─── Diary Stats ───
  const farmerDiaries = getLogsByFarmer(farmerId);
  const recentDiaryCount = farmerDiaries.filter((d) => {
    const diaryDate = new Date(d.ngay);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return diaryDate >= sevenDaysAgo;
  }).length;

  // ─── Pending Loan Stats ───
  const pendingLoans = moneySummary.choKiemTra;
  const approvedLoans = moneySummary.daDuyet;
  const totalPendingActions = pendingLoans + approvedLoans + moneySummary.quaHan + moneySummary.sapDenHan;

  // ─── Counts for badges ───
  const loanBadgeCount = pendingLoans + approvedLoans;
  const debtBadgeCount = moneySummary.quaHan + moneySummary.sapDenHan;
  // Limit-increase requests are no longer a trackable entity in this app — real
  // limit grants happen via Agrida-internal contract review (see task 27 notes).
  const pendingLimitCount = 0;

  // ─── Sheet state ───
  const [activeSheet, setActiveSheet] = useState<string | null>(null);

  // ─── Activities ───
  const recentActivities = buildFarmerActivities(farmerId);

  // ─── KYC label ───
  const kycLabel = kyc ? getKycLabel(kyc.kycStatus) : 'Chưa xác thực';
  const riskLabel = kyc?.riskLevel || 'Tốt';
  const riskColor = getRiskColor(riskLabel);

  const today = new Date();
  const syncDate = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;

  if (!user || user.role !== 'Nông hộ') {
    return <Navigate to="/dang-nhap" replace />;
  }

  if (!farmer) {
    return (
      <div className="px-4 py-6">
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <span className="w-12 h-12 mx-auto flex items-center justify-center mb-3 text-slate-300">
            <i className="ri-user-unfollow-line text-3xl" />
          </span>
          <p className="text-slate-500 text-sm">Không tìm thấy thông tin nông hộ</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-4 pb-4 space-y-5 max-w-[1200px] mx-auto">
      {/* ========== HEADER ========== */}
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wide">Nông hộ Dashboard</p>
          <h2 className="text-lg font-bold text-gray-900 leading-tight truncate">{farmer.name}</h2>
          <p className="text-[10px] text-gray-400 mt-0.5 truncate">{farmer.address}</p>
        </div>
        <span className={`shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ml-2 ${isKycVerified ? 'bg-agrida-50 text-agrida-700' : isKycPending ? 'bg-amber-50 text-amber-700' : isKycNeedMore ? 'bg-orange-50 text-orange-700' : 'bg-red-50 text-red-700'}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${isKycVerified ? 'bg-agrida-500' : isKycPending ? 'bg-amber-500' : isKycNeedMore ? 'bg-orange-500' : 'bg-red-500'}`} />
          {kycLabel}
        </span>
      </div>

      {/* ========== eKYC REMINDER (when not verified) ========== */}
      {!isKycVerified && (
        <CompactAlert
          tone={isKycNeedMore ? 'warning' : isKycFailed ? 'danger' : 'warning'}
          title={
            isKycNeedMore ? 'Cần bổ sung eKYC' :
            isKycFailed ? 'eKYC thất bại' :
            'Chưa xác thực eKYC'
          }
          description={
            isKycNeedMore
              ? 'Hồ sơ eKYC của bạn cần bổ sung thêm thông tin. Vui lòng cập nhật để mở khóa chức năng vay vốn.'
              : isKycFailed
                ? 'Xác thực danh tính thất bại. Vui lòng kiểm tra lại thông tin và thực hiện lại.'
                : 'Hoàn tất eKYC để mở khóa đầy đủ tính năng vay vốn và hạn mức tiền.'
          }
          details={kyc?.kycReason ? [kyc.kycReason] : undefined}
        />
      )}

      {/* ========== HTX INVITE (HTX-initiated — needs the farmer's own accept/decline) ========== */}
      {farmer.membershipStatus === 'Chờ xác nhận' && farmer.membershipInitiatedBy === 'HTX' && (
        <div className="rounded-xl border border-sky-200 bg-sky-50 p-4">
          <p className="text-sm font-bold text-sky-800">Lời mời gia nhập hợp tác xã</p>
          <p className="mt-1 text-xs text-sky-700 leading-relaxed">Một hợp tác xã đã mời bạn tham gia. Xác nhận để hợp tác xã có thể quản lý mùa vụ, quy trình canh tác và hợp đồng bao tiêu cho bạn.</p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={async () => {
                try {
                  await respondToHtxInvite(farmerId, 'ACCEPT');
                  showToast('Đã xác nhận gia nhập hợp tác xã', 'success');
                } catch (e) {
                  showToast(e instanceof Error ? e.message : 'Xác nhận thất bại', 'error');
                }
              }}
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold bg-agrida-600 text-white hover:bg-agrida-700 transition-colors whitespace-nowrap"
            >
              Đồng ý gia nhập
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  await respondToHtxInvite(farmerId, 'DECLINE');
                  showToast('Đã từ chối lời mời', 'info');
                } catch (e) {
                  showToast(e instanceof Error ? e.message : 'Thao tác thất bại', 'error');
                }
              }}
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-medium bg-white border border-sky-200 text-sky-700 hover:bg-sky-100 transition-colors whitespace-nowrap"
            >
              Từ chối
            </button>
          </div>
        </div>
      )}

      {/* ========== HTX MEMBERSHIP PENDING (join-request not yet confirmed) ========== */}
      {farmer.membershipStatus === 'Chờ xác nhận' && farmer.membershipInitiatedBy !== 'HTX' && (
        <CompactAlert
          tone="warning"
          title="Đang chờ HTX xác nhận"
          description="Hồ sơ của bạn đã được ghi nhận. Một số thao tác (gán quy trình canh tác, hợp đồng bao tiêu) sẽ do HTX quản lý sau khi xác nhận gia nhập."
        />
      )}
      {farmer.membershipStatus === 'Từ chối' && (
        <CompactAlert
          tone="danger"
          title="Yêu cầu gia nhập HTX đã bị từ chối"
          description="Vui lòng liên hệ hợp tác xã để biết thêm chi tiết."
          details={farmer.membershipRejectedReason ? [farmer.membershipRejectedReason] : undefined}
        />
      )}

      {/* ========== QUICK STATS ROW ========== */}
      {isKycVerified && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {kyc?.creditScore !== undefined && (
            <div className="bg-white rounded-2xl border border-slate-100 p-4">
              <ImportantMetric
                label="Credit Score"
                value={kyc.creditScore}
                tone={kyc.creditScore >= 700 ? 'emerald' : kyc.creditScore >= 550 ? 'amber' : 'red'}
                size="md"
                note={riskLabel}
              />
            </div>
          )}
          <div className="bg-white rounded-2xl border border-slate-100 p-4">
            <ImportantMetric
              label="Hạn mức tiền"
              value={<CurrencyAmount value={remainingLimit} className="font-bold" />}
              tone="emerald"
              size="md"
              note={`còn ${formatCurrencyCompact(remainingLimit)} / ${formatCurrencyCompact(totalLimit)}`}
            />
          </div>
          <div className={`bg-white rounded-2xl border p-4 ${totalDebt > 0 ? 'border-amber-200' : 'border-slate-100'}`}>
            <ImportantMetric
              label="Dư nợ tiền"
              value={<CurrencyAmount value={totalDebt} className="font-bold" />}
              tone={totalDebt > 0 ? 'amber' : 'slate'}
              size="md"
              note={overdueCount > 0 ? `${overdueCount} khoản quá hạn` : undefined}
            />
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 p-4">
            <ImportantMetric
              label="Mùa vụ"
              value={activeSeasons.length}
              tone="slate"
              size="md"
              note="đang canh tác"
            />
          </div>
        </div>
      )}

      {/* ========== QUICK ACTIONS (Direct BottomSheet) ========== */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-5 rounded-full bg-agrida-500" />
            <h3 className="text-sm font-semibold text-gray-900">Thao tác nhanh</h3>
          </div>
          {totalPendingActions > 0 && (
            <span className="text-[10px] font-bold text-white bg-agrida-600 px-2 py-0.5 rounded-full whitespace-nowrap">
              {totalPendingActions} việc cần chú ý
            </span>
          )}
        </div>
        <ActionIconGrid
          columns={3}
          actions={farmerActions.map((action) => {
            const badgeCount = action.id === 'loan' ? loanBadgeCount
              : action.id === 'limit' ? pendingLimitCount
              : action.id === 'repay' ? debtBadgeCount
              : action.id === 'diary' ? recentDiaryCount
              : 0;

            return {
              id: action.id,
              icon: action.icon,
              label: action.label,
              bgColor: action.bgColor,
              iconColor: action.iconColor,
              badgeCount,
              onClick: () => {
                if (action.id === 'track') { navigate('/nong-ho/vay-von'); return; }
                setActiveSheet(action.id);
              },
            };
          })}
        />
      </div>

      {/* ========== MONEY LIMIT CARD (if has limit) ========== */}
      {isKycVerified && totalLimit > 0 && (
        <button
          type="button"
          onClick={() => navigate('/nong-ho/vay-von')}
          className="w-full bg-white rounded-2xl border border-gray-100 p-4 cursor-pointer transition-all active:scale-[0.98] text-left"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="w-9 h-9 rounded-xl bg-agrida-50 flex items-center justify-center shrink-0">
                <i className="ri-funds-line text-agrida-600 text-base" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">Hạn mức vay tiền</p>
                <p className="text-[10px] text-gray-400 truncate">{activeContracts.length} hợp đồng đang hiệu lực</p>
              </div>
            </div>
          </div>
          <div className="mb-2">
            <p className={`text-lg font-bold leading-none ${remainingLimit > 0 ? 'text-agrida-600' : 'text-red-600'}`}>
              {formatCurrencyFull(remainingLimit)}
            </p>
            <p className="text-[10px] text-gray-400 mt-0.5">còn lại / {formatCurrencyFull(totalLimit)}</p>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
            <div className={`h-full rounded-full transition-all ${limitProgress.barColor}`} style={{ width: `${Math.min(limitPercent, 100)}%` }} />
          </div>
          <p className={`text-[10px] font-medium ${limitProgress.textColor}`}>
            {limitProgress.label || `Đã sử dụng ${formatNumber(limitPercent)}% hạn mức`}
          </p>
        </button>
      )}

      {/* ========== CONTRACTS ========== */}
      {farmerContracts.length > 0 && (
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Hợp đồng của tôi</h3>
            <button type="button" onClick={() => setActiveSheet('contracts')} className="text-xs font-semibold text-agrida-600">Xem tất cả</button>
          </div>
          <div className="space-y-2">
            {farmerContracts.slice(0, 2).map((contract) => {
              const relatedSeasons = farmerSeasons.filter((season) => contract.seasonIds?.includes(season.id));
              return (
                <button
                  key={contract.id}
                  type="button"
                  onClick={() => setActiveSheet('contracts')}
                  className="w-full rounded-xl border border-gray-100 bg-white p-3 text-left transition active:scale-[0.99]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-slate-900">{contract.soHopDong}</p>
                      <p className="mt-0.5 line-clamp-1 text-[10px] text-slate-400">{contract.noiDung}</p>
                    </div>
                    {/* Feedback 08 A2: keep the real status visible alongside the
                        expiring-soon warning instead of one hiding the other. */}
                    <div className="flex shrink-0 items-center gap-1">
                      <span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${contract.status === 'Đang hiệu lực' ? 'bg-agrida-100 text-agrida-700' : 'bg-slate-100 text-slate-600'}`}>{contract.status}</span>
                      {contract.expiringSoon && contract.status === 'Đang hiệu lực' && (
                        <span className="rounded-full bg-amber-100 px-2 py-1 text-[10px] font-semibold text-amber-700">Sắp hết hạn</span>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    <ContractMiniMetric label="Giá trị" value={formatCurrencyCompact(contract.giaTri)} />
                    <ContractMiniMetric label="Đã cấp" value={formatCurrencyCompact(contract.hanMucDaCap || 0)} tone="amber" />
                    <ContractMiniMetric label="Mùa vụ" value={String(relatedSeasons.length || contract.seasonIds?.length || 0)} tone="emerald" />
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* ========== OVERDUE WARNING ========== */}
      {overdueCount > 0 && (
        <CompactAlert
          tone="danger"
          title={`Cảnh báo nợ quá hạn: ${overdueCount} khoản, ${formatCurrencyFull(overdueDebt)}`}
          description="Vui lòng thanh toán sớm để tránh ảnh hưởng đến điểm tín dụng và khả năng vay vốn trong tương lai."
        />
      )}

      {/* ========== SEASON STATUS ========== */}
      {activeSeasons.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Mùa vụ đang canh tác</h3>
          <div className="space-y-2">
            {activeSeasons.map((season) => {
              const field = state.fields.find((f) => f.id === season.fieldId);
              const seasonDiaries = farmerDiaries.filter((d) => d.seasonId === season.id);
              const lastDiaryDate = seasonDiaries.length > 0
                ? seasonDiaries.sort((a, b) => new Date(b.ngay).getTime() - new Date(a.ngay).getTime())[0].ngay
                : null;
              const daysSinceLastDiary = lastDiaryDate
                ? Math.floor((new Date().getTime() - new Date(lastDiaryDate).getTime()) / (1000 * 60 * 60 * 24))
                : null;
              const noDiaryWarning = daysSinceLastDiary !== null && daysSinceLastDiary >= 7;

              return (
                <div key={season.id} className="bg-white rounded-xl border border-gray-100 p-3 flex items-center gap-3">
                  <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${season.status === 'Sắp thu hoạch' ? 'bg-amber-50' : 'bg-agrida-50'}`}>
                    <i className={`${season.status === 'Sắp thu hoạch' ? 'ri-shopping-basket-line text-amber-600' : 'ri-plant-line text-agrida-600'} text-lg`} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <p className="text-sm font-semibold text-gray-900 truncate">{season.name}</p>
                      <span className={`shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${season.status === 'Sắp thu hoạch' ? 'bg-amber-100 text-amber-700' : 'bg-agrida-100 text-agrida-700'}`}>
                        {season.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-gray-400">
                      <span>{field?.name || season.cayTrong}</span>
                      <span>•</span>
                      <span>{seasonDiaries.length} nhật ký</span>
                      {noDiaryWarning && (
                        <>
                          <span>•</span>
                          <span className="text-amber-600 font-medium">{daysSinceLastDiary} ngày chưa ghi nhật ký</span>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate('/nong-ho/nhat-ky')}
                    className="shrink-0 w-8 h-8 flex items-center justify-center text-gray-300 hover:text-agrida-600 transition-colors"
                  >
                    <i className="ri-arrow-right-s-line text-lg" />
                  </button>
                </div>
              );
            })}
          </div>
          {preparingSeasons.length > 0 && (
            <p className="text-[10px] text-gray-400 mt-2 ml-1">
              + {preparingSeasons.length} mùa vụ đang chuẩn bị
            </p>
          )}
        </div>
      )}

      {/* ========== HOẠT ĐỘNG GẦN ĐÂY ========== */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Hoạt động gần đây</h3>
        <div className="space-y-0">
          {recentActivities.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-xs text-gray-400">Chưa có hoạt động nào gần đây</p>
            </div>
          ) : (
            recentActivities.map((act, idx) => {
              const st = activityStyles[act.color];
              return (
                <div key={act.id} className="relative flex items-start gap-3 py-2.5">
                  {idx < recentActivities.length - 1 && (
                    <div className="absolute left-[12px] top-8 bottom-0 w-px bg-gray-100" />
                  )}
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${st.iconBg}`}>
                    <i className={`${act.icon} ${st.iconColor} text-[11px]`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-[10px] text-gray-400 shrink-0">{act.time}</span>
                    </div>
                    <p className="text-[11px] text-gray-500 leading-snug line-clamp-2 break-words">{act.message}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <p className="text-[10px] text-gray-300 text-center">Đồng bộ: {syncDate}</p>

      {/* ========== QUICK ACTION SHEETS ========== */}
      <ApplyLoanSheet
        open={activeSheet === 'loan'}
        onClose={() => setActiveSheet(null)}
        farmerId={farmerId}
        kyc={kyc}
      />

      <ApplyLimitSheet
        open={activeSheet === 'limit'}
        onClose={() => setActiveSheet(null)}
        farmerId={farmerId}
        farmerName={farmer.name}
      />

      <QuickDiarySheet
        open={activeSheet === 'diary'}
        onClose={() => setActiveSheet(null)}
        farmerId={farmerId}
      />

      <RepayDebtSheet
        open={activeSheet === 'repay'}
        onClose={() => setActiveSheet(null)}
        farmerId={farmerId}
      />

      <BottomSheet open={activeSheet === 'contracts'} onClose={() => setActiveSheet(null)} title="Hợp đồng của tôi">
        <div className="space-y-3">
          {farmerContracts.map((contract) => {
            const relatedSeasons = farmerSeasons.filter((season) => contract.seasonIds?.includes(season.id));
            return (
              <article key={contract.id} className="rounded-xl border border-slate-200 bg-white p-3.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h4 className="truncate text-sm font-bold text-slate-900">{contract.soHopDong}</h4>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500">{contract.noiDung}</p>
                  </div>
                  {/* Feedback 08 A2: keep the real status visible alongside the
                      expiring-soon warning instead of one hiding the other. */}
                  <div className="flex shrink-0 items-center gap-1">
                    <span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${contract.status === 'Đang hiệu lực' ? 'bg-agrida-100 text-agrida-700' : 'bg-slate-100 text-slate-600'}`}>{contract.status}</span>
                    {contract.expiringSoon && contract.status === 'Đang hiệu lực' && (
                      <span className="rounded-full bg-amber-100 px-2 py-1 text-[10px] font-semibold text-amber-700">Sắp hết hạn</span>
                    )}
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <ContractDetail label="Giá trị" value={formatCurrencyFull(contract.giaTri)} />
                  <ContractDetail label="Hạn mức đã cấp" value={formatCurrencyFull(contract.hanMucDaCap || 0)} />
                  <ContractDetail label="Ngày hiệu lực" value={contract.ngayHieuLuc ? formatDate(contract.ngayHieuLuc) : formatDate(contract.ngayKy)} />
                  <ContractDetail label="Ngày hết hạn" value={formatDate(contract.ngayHetHan)} />
                </div>
                {relatedSeasons.length > 0 && (
                  <div className="mt-3 rounded-xl bg-slate-50 p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Mùa vụ trong hợp đồng</p>
                    <div className="mt-2 space-y-1">
                      {relatedSeasons.map((season) => (
                        <p key={season.id} className="text-xs font-medium text-slate-700">{season.name} · {season.status}</p>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-3 rounded-xl border border-slate-200 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Tài liệu đính kèm</p>
                  {contract.attachments && contract.attachments.length > 0 ? (
                    <div className="mt-2 space-y-2">
                      {contract.attachments.map((file) => (
                        <div key={file.fileId} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
                          <span className="truncate text-xs font-semibold text-slate-700">{file.caption || (file.type === 'image' ? 'Ảnh đính kèm' : 'Tài liệu')}</span>
                          <i className="ri-file-pdf-2-line text-rose-500" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-1 text-xs text-slate-500">Chưa có tài liệu đính kèm trong dữ liệu mô phỏng.</p>
                  )}
                </div>
              </article>
            );
          })}
          {farmerContracts.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-200 py-8 text-center text-xs text-slate-400">Chưa có hợp đồng</div>
          )}
          <button
            type="button"
            onClick={() => {
              setActiveSheet(null);
              navigate('/nong-ho/han-muc');
            }}
            className="min-h-11 w-full rounded-xl bg-agrida-600 px-4 text-sm font-bold text-white"
          >
            Mở module Hạn mức & hợp đồng
          </button>
        </div>
      </BottomSheet>

      <FirstLoginQuickGuide roleKey="farmer" title="6 thao tác nhanh của Nông hộ" items={farmerGuideItems} />
    </div>
  );
}

function ContractMiniMetric({ label, value, tone = 'slate' }: { label: string; value: string; tone?: 'slate' | 'amber' | 'emerald' }) {
  const color = tone === 'amber' ? 'text-amber-700' : tone === 'emerald' ? 'text-agrida-700' : 'text-slate-800';
  return <div className="min-w-0 rounded-xl bg-slate-50 p-2 text-center"><p className="text-[10px] text-slate-400">{label}</p><p className={`mt-0.5 truncate text-xs font-bold ${color}`}>{value}</p></div>;
}

function ContractDetail({ label, value }: { label: string; value: string }) {
  return <div className="min-w-0 rounded-xl bg-slate-50 p-2.5"><p className="text-[10px] text-slate-400">{label}</p><p className="mt-0.5 text-xs font-semibold text-slate-700 text-safe-normal">{value}</p></div>;
}
